import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Mail, Calendar, Linkedin, MessageSquare, MapPin, Clock } from 'lucide-react';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';

export function Contact() {
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Contact Us"
          description="Get in touch with Situs Canada. Questions about Orca AI, data sovereignty, or enterprise pricing? We respond within 24 hours."
          canonical="https://situsai.ca/contact"
          ogImage="https://situsai.ca/logo.svg"
        />
        <JsonLd data={createBreadcrumbSchema('/contact')} />

        {/* Hero */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <MessageSquare className="w-4 h-4" /> Let's talk
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Get in touch
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-2xl mx-auto">
            Questions about Orca, data sovereignty, or how AI can help your practice? We respond within 24 hours.
          </p>
        </section>

        {/* Contact methods */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <a
              href="mailto:carlo@situsai.ca"
              className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:border-black/20 transition-colors group"
            >
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-sm text-gray-500 mb-3">For detailed inquiries</p>
              <span className="text-sm font-medium text-[var(--color-csai-primary)]">carlo@situsai.ca</span>
            </a>

            <a
              href="https://calendly.com/rcarloc029/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:border-black/20 transition-colors group"
            >
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Book a Demo</h3>
              <p className="text-sm text-gray-500 mb-3">30-minute walkthrough</p>
              <span className="text-sm font-medium text-[var(--color-csai-primary)]">Schedule a time</span>
            </a>

            <a
              href="https://www.linkedin.com/company/situs-canada"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:border-black/20 transition-colors group"
            >
              <div className="w-12 h-12 bg-[#0077b5] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform">
                <Linkedin className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">LinkedIn</h3>
              <p className="text-sm text-gray-500 mb-3">Follow for updates</p>
              <span className="text-sm font-medium text-[var(--color-csai-primary)]">Situs Canada</span>
            </a>
          </div>
        </section>

        {/* Contact form */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto mb-16">
          <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Send us a message</h2>
            <p className="text-sm text-gray-500 mb-8">We'll get back to you within one business day.</p>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Message sent</h3>
                <p className="text-gray-500">We'll respond within 24 hours. Check your inbox at the email you provided.</p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const name = (form.elements.namedItem('name') as HTMLInputElement)?.value;
                  const email = (form.elements.namedItem('email') as HTMLInputElement)?.value;
                  const subject = (form.elements.namedItem('subject') as unknown as HTMLSelectElement)?.value;
                  const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value;

                  const mailtoSubject = encodeURIComponent(`[Situs Canada] ${subject}: ${name}`);
                  const mailtoBody = encodeURIComponent(`From: ${name} (${email})\n\n${message}`);
                  window.location.href = `mailto:carlo@situsai.ca?subject=${mailtoSubject}&body=${mailtoBody}`;
                  setSubmitted(true);
                }}
                className="space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
                    <input
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-all"
                      placeholder="you@firm.ca"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Topic</label>
                  <select
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-all bg-white"
                  >
                    <option value="">Select a topic</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Product Demo">Product Demo Request</option>
                    <option value="Data Security">Data Security Questions</option>
                    <option value="Enterprise/Firm">Enterprise / Firm Pricing</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Support">Technical Support</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-400 transition-all resize-none"
                    placeholder="How can we help?"
                  />
                </div>

                <Button type="submit" className="rounded-full px-8 w-full sm:w-auto">
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </section>

        {/* Info bar */}
        <section className="py-8 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Montreal, Quebec</p>
                <p className="text-xs text-gray-500">100% Canadian operated</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Response time</p>
                <p className="text-xs text-gray-500">Within 24 hours, business days</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Support</p>
                <p className="text-xs text-gray-500">carlo@situsai.ca</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
